import type { VoidFunctionComponent } from 'react';

import { window } from '../../../../globals';
import useListMfaMethods from '../../../../hooks/api/auth/mfa/useListMfaMethods';
import Loader from '../../../generic/Loader';
import Header from '../../../shared/settings/SettingsPageHeader';
import type { MfaMethodType } from '../../../../../shared/model-typings/mfaMethod';

import EmailMethodSection from './EmailMethodSection';
import TotpMethodSection from './TotpMethodSection';

export const getTitleAndDescriptionByType = (type: MfaMethodType) => {
  switch (type) {
    case 'email':
      return {
        title: 'Email Verification Code',
        description: 'Receive a one-time code by email to verify your identity.\n' +
          'A simple and convenient option that requires no additional apps.',
      };
    case 'totp':
      return {
        title: 'Authenticator App (TOTP)',
        description: 'Add a time-based one-time code using an authenticator app (e.g., Google Authenticator).\n' +
          'This method offers the highest level of security and resilience against account compromise.',
      };
    default:
      return {
        title: '',
        description: '',
      };
  }
};

/**
 * Multi-Factor Authentication settings page.
 * Allows users to set up and manage MFA methods.
 */
const MfaSettings: VoidFunctionComponent = () => {
  const { data, isLoading } = useListMfaMethods();

  if (isLoading) {
    return <Loader />;
  }

  const mfaMethods = data?.methods || [];
  const totpMethod = mfaMethods.find((m) => m.type === 'totp');
  const emailMethod = mfaMethods.find((m) => m.type === 'email');

  return (
    <>
      <Header heading="Multi-Factor Authentication">
        <Header.Paragraph>
          Increase your account&#39;s security by using multiple authentication methods to verify your identity.
        </Header.Paragraph>

      </Header>

      <TotpMethodSection method={totpMethod} />

      {window?.user?.email_verified &&
        <EmailMethodSection method={emailMethod} />}
    </>
  );
};

export default MfaSettings;
